// Delivery promise (snippets/delivery-promise.liquid): arrival date range from the order to the door.
(() => {
  if (customElements.get('delivery-promise')) return;

  // Working days from order to door, owner-approved 2026-10-20 (memory delivery-times). [Casapulla, European warehouse]
  // Every range is at most 3 days wide. Same table as the shipping policy page and the shop assistant.
  const GROUPS = {
    IT: ['IT'],
    near: ['AT', 'BE', 'DE', 'FR', 'LU', 'NL', 'SI', 'HR'],
    mid: ['CZ', 'DK', 'ES', 'HU', 'PL', 'PT', 'SK'],
    far: ['BG', 'EE', 'FI', 'GR', 'IE', 'LT', 'LV', 'RO', 'SE'],
    islands: ['CY', 'MT'],
    CH: ['CH'],
  };
  const TOTALS = {
    IT: [[2, 4], [6, 8]], near: [[3, 5], [7, 9]], mid: [[4, 6], [8, 10]], far: [[5, 7], [9, 11]],
    islands: [[6, 8], [10, 12]], CH: [[4, 6], [8, 10]], world: [[5, 7], [9, 11]],
  };
  const totalsFor = (cc) => TOTALS[Object.keys(GROUPS).find((k) => GROUPS[k].includes(cc)) || 'world'];
  const CUTOFF_HOUR = 13; // Italian time
  // Italian national holidays (MM-DD) plus Easter Monday per year.
  const FIXED = ['01-01', '01-06', '04-25', '05-01', '06-02', '08-15', '11-01', '12-08', '12-25', '12-26'];
  const EASTER_MONDAY = { 2026: '04-06', 2027: '03-29', 2028: '04-17' };

  const romeNow = () => {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Rome', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' })
      .formatToParts(new Date()).map((x) => [x.type, x.value]));
    return { date: new Date(Date.UTC(+p.year, +p.month - 1, +p.day)), hour: +p.hour };
  };
  const md = (d) => d.toISOString().slice(5, 10);
  const working = (d) => {
    const w = d.getUTCDay();
    return w !== 0 && w !== 6 && !FIXED.includes(md(d)) && EASTER_MONDAY[d.getUTCFullYear()] !== md(d);
  };
  const addDay = (d) => new Date(d.getTime() + 86400000);
  const addWorking = (d, n) => { let x = d; for (let i = 0; i < n; i++) { do { x = addDay(x); } while (!working(x)); } return x; };

  // Cart (snippets/cart-delivery.liquid), owner 2026-10-20: a short date under every item ("gio 22 - lun 26 ott"),
  // and one line for carts that ship in 2 parcels (Casapulla stock first, everything else from the European warehouse).
  const shortRange = (cc, shop, locale) => {
    const [min, max] = totalsFor(cc)[shop ? 0 : 1];
    const now = romeNow();
    let start = now.date;
    if (!working(start) || now.hour >= CUTOFF_HOUR) { do { start = addDay(start); } while (!working(start)); }
    const from = addWorking(start, min), to = addWorking(start, max);
    const f = (d, month) => new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'short', day: 'numeric', ...(month ? { month: 'short' } : {}) }).format(d);
    // English ("28 Wed") and Spanish ("mié 28" next to "vie, 30 oct") read badly without the month on both ends.
    return `${f(from, /^(en|es)/.test(locale) || from.getUTCMonth() !== to.getUTCMonth())} - ${f(to, true)}`;
  };
  const origins = {};
  const originOf = (id) => {
    if (!origins[id]) {
      // v2 (2026-11-03): adds "service"; the new key drops origins cached before services existed.
      const key = `evo-origin2-${id}`;
      let cached = null;
      try { cached = sessionStorage.getItem(key); } catch (e) {}
      origins[id] = cached ? Promise.resolve(cached) : fetch(`${window.Shopify?.routes?.root || '/'}variants/${id}/?section_id=delivery-origin`)
        .then((r) => r.text()).then((t) => { const o = (t.match(/data-origin="(shop|service)"/) || [])[1] || 'warehouse'; try { sessionStorage.setItem(key, o); } catch (e) {} return o; })
        .catch(() => 'warehouse');
    }
    return origins[id];
  };
  // Services (Montaggio completo) ship with the goods: they take the goods' origin, so they never split a cart.
  const settle = (kinds) => {
    const to = kinds.includes('shop') ? 'shop' : 'warehouse';
    return kinds.map((k) => (k === 'service' ? to : k));
  };
  // Group the items of one list under date headings: Casapulla stock first, then everything else.
  // The snippet also prints its stylesheet and script, so look past them for the item list.
  const listAfter = (cfg) => { let n = cfg.nextElementSibling; while (n && !n.matches('.cart-lines, .mini-cart-item-list')) n = n.nextElementSibling; return n; };
  const groupList = async (cfg) => {
    const list = listAfter(cfg);
    if (!list) return;
    const d = cfg.dataset;
    const items = [...list.querySelectorAll(':scope > .js-cart-item[data-variant-id]')];
    if (!items.length) return;
    const raw = await Promise.all(items.map((it) => originOf(it.dataset.variantId)));
    const kinds = settle(raw);
    list.querySelectorAll(':scope > .cart-delivery-heading').forEach((h) => h.remove());
    list.querySelectorAll(':scope > .cart-delivery-note').forEach((h) => h.remove());
    const tag = list.tagName === 'UL' ? 'li' : 'div';
    // A mixed cart ships as ONE parcel by default everywhere, Italy included (owner 2026-10-21): everything under the
    // later date; the paid "Spedizione veloce in 2 pacchi" is chosen at checkout.
    const single = kinds.includes('shop') && kinds.includes('warehouse');
    (single ? ['all'] : ['shop', 'warehouse']).forEach((origin) => {
      const members = origin === 'all' ? items : items.filter((_, k) => kinds[k] === origin);
      if (!members.length) return;
      const h = document.createElement(tag);
      h.className = 'cart-delivery-heading';
      h.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6.5h11v9H3z"/><path d="M14 9.5h3.6l3.4 3.4v2.6h-7"/><circle cx="7" cy="17.5" r="1.8"/><circle cx="17.5" cy="17.5" r="1.8"/></svg>';
      // Plural by the total quantity in the group (Shopify count forms 1, 2, 5 cover one / other / few / many).
      // Services are not parcels: "Arriva" for one bike with its assembly.
      const qty = members.reduce((n, it) => n + (raw[items.indexOf(it)] === 'service' ? 0 : (parseInt(it.querySelector('input[name="updates[]"]')?.value, 10) || 1)), 0) || 1;
      const cat = new Intl.PluralRules(d.locale).select(qty);
      const form = cat === 'one' ? d.arrivesOne : cat === 'many' ? d.arrivesFive : d.arrivesTwo;
      h.append(form.replace('%RANGE%', shortRange(d.cc, origin === 'shop', d.locale)) + (d.world === 'true' ? '. ' + d.customs : ''));
      members.forEach((it, k) => { it.classList.add('dp-in-group'); it.classList.toggle('dp-group-last', k === members.length - 1); });
      list.append(h, ...members);
    });
    if (single && d.splitNote) {
      const n = document.createElement(tag);
      n.className = 'cart-delivery-note';
      n.textContent = d.splitNote;
      list.append(n);
    }
  };
  // Team and checkout copy of the groups (owner 2026-10-20): cart attributes, updated whenever the cart changes.
  // "Spedizione" (visible on the order, Italian for the team) and "_delivery_groups" (JSON for the checkout extension).
  let attrTimer = null;
  const syncAttributes = () => {
    clearTimeout(attrTimer);
    attrTimer = setTimeout(async () => {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const cart = await (await fetch(`${root}cart.js`)).json();
        const cfg = document.querySelector('template[data-dp-groups]');
        const cc = cfg?.dataset.cc || 'IT';
        const groups = [];
        if (cart.items.length) {
          const kinds = settle(await Promise.all(cart.items.map((it) => originOf(it.variant_id))));
          ['shop', 'warehouse'].forEach((origin) => {
            const items = cart.items.filter((_, k) => kinds[k] === origin);
            if (!items.length) return;
            const [min, max] = totalsFor(cc)[origin === 'shop' ? 0 : 1];
            groups.push({ origin, min, max, range_it: shortRange(cc, origin === 'shop', 'it'), items: items.map((it) => ({ variant_id: it.variant_id, title: it.product_title, quantity: it.quantity })) });
          });
        }
        const label = (g) => `${g.origin === 'shop' ? 'Casapulla' : 'magazzino europeo'} ${g.range_it} (${g.items.map((i) => i.title).join(', ')})`;
        // A mixed cart is 1 parcel by default everywhere (owner 2026-10-21); the paid fast option shows on the order's shipping line.
        // Never write the literal "2 pacchi" here: the Flow tag and the email line match on it ("tutto insieme" marks these orders).
        const single = groups.length === 2;
        const all = groups.flatMap((g) => g.items.map((i) => i.title)).join(', ');
        const visible = !groups.length ? '' : single
          ? `1 pacco (tutto insieme): spedire tutto insieme quando arriva il magazzino europeo, consegna ${groups[1].range_it} (${all}). Se nella spedizione dell'ordine il cliente ha scelto la spedizione veloce in due pacchi: Casapulla subito, il resto dopo.`
          : `${groups.length === 1 ? '1 pacco' : '2 pacchi'}: ${groups.map(label).join(' + ')}`;
        const hidden = groups.length ? JSON.stringify({ country: cc, parcels: single ? 1 : groups.length, split_option: single, shipping_paid_once: true, groups }) : '';
        if (cart.attributes?.Spedizione === visible && cart.attributes?._delivery_groups === hidden) return;
        await fetch(`${root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { Spedizione: visible, _delivery_groups: hidden } }) });
      } catch (e) {}
    }, 400);
  };
  const fillCart = () => {
    document.querySelectorAll('template[data-dp-groups]').forEach((cfg) => {
      const list = listAfter(cfg);
      if (!list || list.dataset.dpGrouped === String(list.querySelectorAll(':scope > .js-cart-item').length) && list.querySelector(':scope > .cart-delivery-heading')) return;
      list.dataset.dpGrouped = String(list.querySelectorAll(':scope > .js-cart-item').length);
      groupList(cfg);
      syncAttributes();
    });
  };
  fillCart();
  new MutationObserver(fillCart).observe(document.documentElement, { childList: true, subtree: true });

  class DeliveryPromise extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-delivery-variants]').textContent);
      this.i18n = JSON.parse(this.querySelector('[data-delivery-i18n]').textContent);
      this.render(this.dataset.variant);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsub = subscribe(PUB_SUB_EVENTS.productVariantChange, (data) => {
          if (data?.variant?.id && this.variants[data.variant.id]) this.render(String(data.variant.id));
        });
      }
    }
    disconnectedCallback() { this.unsub?.(); }

    render(id) {
      const v = this.variants[id];
      if (!v || !v.a) { this.hidden = true; return; }
      this.hidden = false;
      const zone = this.dataset.zone;
      const [min, max] = totalsFor(this.dataset.cc)[v.s ? 0 : 1];
      const now = romeNow();
      // The order day counts as day 0 only on a working day before the cut-off.
      let start = now.date;
      if (!working(start) || now.hour >= CUTOFF_HOUR) { do { start = addDay(start); } while (!working(start)); }
      const from = addWorking(start, min), to = addWorking(start, max);
      const locale = this.dataset.locale || 'it';
      const fmt = (d) => new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' }).format(d);
      const range = this.i18n.arrives.replace('{{ from }}', fmt(from)).replace('{{ to }}', fmt(to));
      this.querySelector('[data-delivery-date]').textContent = range;
      const origin = this.querySelector('[data-delivery-origin]');
      const parts = [this.i18n.to.replace('{{ country }}', this.dataset.country)];
      origin.textContent = '';
      origin.append(parts[0] + '. ');
      const src = document.createElement('span');
      src.textContent = v.s ? this.i18n.shop : this.i18n.warehouse;
      if (v.s) src.className = 'is-fast';
      origin.append(src);
      if (zone === 'world') origin.append(' ' + this.i18n.customs);
    }
  }
  customElements.define('delivery-promise', DeliveryPromise);

  // PayPal Pay Later line: Shopify injects it at the top of the product form; move it under the payment buttons.
  const movePayPal = () => {
    const msg = document.getElementById('paypal-upstream-messaging-product');
    const target = document.querySelector('.product-actions-buy-now') || document.querySelector('.product-actions');
    if (!msg || !target || msg.dataset.moved) return !!(msg && msg.dataset.moved);
    msg.dataset.moved = '1';
    msg.classList.add('delivery-paypal-moved');
    target.after(msg);
    return true;
  };
  if (!movePayPal()) {
    const mo = new MutationObserver(() => { if (movePayPal()) mo.disconnect(); });
    mo.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(() => mo.disconnect(), 15000);
  }
})();

// Instalment widgets (owner 2026-10-20): Scalapay as a plain line like PayPal and HeyLight, theme font, no box.
(() => {
  const CSS = `article{border:0!important;box-shadow:none!important;padding:0!important;border-radius:0!important;background:transparent!important}
    .py-4{padding-top:0!important;padding-bottom:0!important}
    .font-scalapay-poppins,*{font-family:inherit!important}
    .text-base,.text-sm{font-size:13px!important;line-height:20px!important}
    .bg-sp-exp5-light-gray{display:none!important}
    article,.flex-nowrap{justify-content:center!important}
    .flex-1{flex:0 1 auto!important}`;
  const style = (w) => {
    const sh = w.shadowRoot;
    if (!sh || sh.querySelector('style[data-evo]')) return !!sh;
    const s = document.createElement('style'); s.dataset.evo = '1'; s.textContent = CSS; sh.append(s);
    return true;
  };
  const run = () => document.querySelectorAll('scalapay-widget').forEach(style);
  run();
  const mo = new MutationObserver(run);
  mo.observe(document.documentElement, { childList: true, subtree: true });
  setTimeout(() => mo.disconnect(), 15000);
})();
